import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import { Pencil } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import {
  AlineAvatar,
  AlineTooltip,
  ConfirmationDialog,
  ToggleSwitchWithIcon,
} from 'src/components';
import { AlineSmallIcon } from 'src/components/icons/AlineSmallIcon';
import { PeopleSmallIcon } from 'src/components/icons/PeopleSmallIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { UserSmallIcon } from 'src/components/icons/UserSmallIcon';
import { SearchInput } from 'src/components/SearchInput';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserId } from 'src/gql/hooks/user';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';

import { getEditorPopoverStyles, getEditorSearchStyles } from './utils';
import { usePopoverDimensions } from '../../chat-hooks/usePopoverPosition';
import { $chatThreadState } from '../../chat-state';

const useStyles = makeStyles()(theme => {
  const sharedSearchStyles = getEditorSearchStyles(theme);
  const sharedPopoverStyles = getEditorPopoverStyles(theme);
  return {
    ...sharedSearchStyles,
    header: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    headerTitle: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    popoverPaper: {
      ...sharedPopoverStyles.popoverPaper,
      // Width is set dynamically via inline style based on available space
    },
    promptActions: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    promptContent: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.5),
      justifyContent: 'space-between',
      overflow: 'hidden',
      width: '100%',
    },
    promptContentLeft: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: theme.spacing(1),
      minWidth: 0,
    },
    promptIcon: {
      alignItems: 'center',
      alignSelf: 'center',
      display: 'flex',
      flexShrink: 0,
      height: 24,
      justifyContent: 'center',
      width: 24,
    },
    promptItem: {
      '&:hover': {
        backgroundColor: theme.appColors.background,
      },
      alignItems: 'center',
      border: theme.appBorders.outline,
      borderRadius: 12,
      boxSizing: 'border-box',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      height: 40,
      overflow: 'hidden',
      padding: theme.spacing(0, 1.5),
    },
    promptItemSelected: {
      backgroundColor: theme.appColors.hoverBackground,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    promptText: {
      display: 'flex',
      flex: 1,
      minWidth: 0,
    },
    promptTextField: {
      flex: 1,
      minWidth: 0,
      position: 'relative',
      zIndex: 1,
    },
    promptsContainer: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      gap: theme.spacing(0.75),
      overflowY: 'auto',
    },
    tab: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      alignItems: 'center',
      borderRadius: 8,
      cursor: 'pointer',
      display: 'flex',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.25, 1),
      transition: 'all 0.2s',
    },
    tabActive: {
      backgroundColor: theme.appColors.hoverBackground,
      color: theme.palette.primary.main,
    },
    tabInactive: {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.common.black,
    },
    tabsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
  };
});

export type PromptTab = 'my-saved' | 'aline' | 'org';

interface TabConfig {
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  id: PromptTab;
  label: string;
}

const TABS: TabConfig[] = [
  {
    icon: UserSmallIcon,
    id: 'my-saved',
    label: 'Me',
  },
  {
    icon: PeopleSmallIcon,
    id: 'org',
    label: 'Org',
  },
  { icon: AlineSmallIcon, id: 'aline', label: 'Aline' },
];

type SavedPrompt = Apollo.GetSavedPromptsQuery['savedPrompts'][number];
type OnShareClickFn = (
  promptId: string,
  options: {
    e: React.MouseEvent;
    scope: Apollo.SavedPromptScope;
  }
) => Promise<void>;

interface PromptItemProps {
  activeTab: PromptTab;
  classes: ReturnType<typeof useStyles>['classes'];
  currentUserId: string | null;
  deleteLoading: boolean;
  editingPromptId: string | null;
  onDeletePrompt: (promptId: string, e: React.MouseEvent) => void;
  onPromptClick: (promptText: string, promptId: string, title: string, isEditable: boolean) => void;
  onShareClick: OnShareClickFn;
  onUpdateTitle: (promptId: string, newTitle: string) => Promise<void>;
  prompt: SavedPrompt;
}

function PromptItem({
  activeTab,
  classes,
  currentUserId,
  deleteLoading,
  editingPromptId,
  onDeletePrompt,
  onPromptClick,
  onShareClick,
  onUpdateTitle,
  prompt,
}: PromptItemProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const isSelected = prompt.id === editingPromptId;
  const isOwner = prompt.owner.id === currentUserId;
  const [editedTitle, setEditedTitle] = useState(prompt.title);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isOrgScope = prompt.scope === Apollo.SavedPromptScope.Org;
  const isAlinePrompt = prompt.scope === Apollo.SavedPromptScope.Aline;
  // Only show edit/delete actions for prompts you own.
  const showActions = mouseOver && !isEditing && isOwner && activeTab === 'my-saved';
  const toggledScope = isOrgScope ? Apollo.SavedPromptScope.Personal : Apollo.SavedPromptScope.Org;
  const showToggleSwitch = showActions && !isAlinePrompt;

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.select();
  }, [isEditing]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeletePrompt(prompt.id, e);
    setConfirmDeleteOpen(false);
  };

  const handleSave = useCallback(async () => {
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setEditedTitle(prompt.title);
      setIsEditing(false);
      return;
    }

    if (trimmedTitle === prompt.title) {
      setIsEditing(false);
      return;
    }

    setUpdating(true);
    await onUpdateTitle(prompt.id, trimmedTitle);
    setUpdating(false);
    setIsEditing(false);
    setEditedTitle(trimmedTitle);
  }, [editedTitle, prompt.id, prompt.title, onUpdateTitle]);

  const handleCancel = useCallback(() => {
    setEditedTitle(prompt.title);
    setIsEditing(false);
  }, [prompt.title]);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          await handleSave();
          break;
        case 'Escape':
          e.preventDefault();
          handleCancel();
          break;
        default:
          break;
      }
    },
    [handleSave, handleCancel]
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditedTitle(prompt.title);
      setIsEditing(true);
    },
    [prompt.title]
  );

  const handleItemClick = useCallback(() => {
    // Only owners can edit prompts...
    if (!isEditing) onPromptClick(prompt.text, prompt.id, prompt.title, isOwner);
  }, [isEditing, onPromptClick, prompt.text, prompt.id, prompt.title, isOwner]);

  return (
    <Box
      className={clsx(classes.promptItem, isSelected && classes.promptItemSelected)}
      onClick={handleItemClick}
      onKeyDown={e => {
        if (isEditing) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleItemClick();
        }
      }}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <div className={classes.promptContent}>
        <div className={classes.promptContentLeft}>
          <PromptOwner prompt={prompt} />
          {isEditing ? (
            <InputBase
              className={classes.promptTextField}
              disabled={updating}
              inputRef={inputRef}
              value={editedTitle}
              onBlur={handleSave}
              onChange={e => setEditedTitle(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <div className={classes.promptText}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 500,
                }}
                variant="body2"
              >
                {prompt.title}
              </Typography>
            </div>
          )}
        </div>
        {showActions && (
          <div className={classes.promptActions}>
            <AlineTooltip arrow placement="top" title="Rename prompt">
              <div>
                <IconButton size="small" style={{ padding: 6 }} onClick={handleEditClick}>
                  <Pencil size={16} />
                </IconButton>
              </div>
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title="Delete prompt">
              <div>
                <IconButton
                  disabled={deleteLoading}
                  size="small"
                  style={{ padding: 6 }}
                  onClick={handleDeleteClick}
                >
                  <TrashcanIcon fontSize="small" />
                </IconButton>
              </div>
            </AlineTooltip>
          </div>
        )}
        {showToggleSwitch && (
          <AlineTooltip
            arrow
            placement="top"
            title={
              isOrgScope
                ? 'Prompt shared with organization. Click to make private.'
                : 'Prompt is private. Click to share with organization.'
            }
          >
            <ToggleSwitchWithIcon
              toggle={e => onShareClick(prompt.id, { e, scope: toggledScope })}
              toggled={isOrgScope}
            />
          </AlineTooltip>
        )}
      </div>
      <ConfirmationDialog
        message="Are you sure you want to delete this prompt? This action cannot be undone."
        open={confirmDeleteOpen}
        primaryText="Delete"
        title="Delete prompt?"
        onClick={e => e.stopPropagation()}
        onClickPrimary={handleConfirmDelete}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </Box>
  );
}

function PromptOwner({ prompt }: { prompt: SavedPrompt }): JSX.Element {
  const { classes } = useStyles();

  switch (prompt.scope) {
    case Apollo.SavedPromptScope.Aline:
      return (
        <div className={classes.promptIcon}>
          <AlineTooltip arrow placement="top" title="Owned by Aline">
            <AlineSmallIcon style={{ height: 20, width: 20 }} />
          </AlineTooltip>
        </div>
      );
    default:
      return (
        <AlineTooltip arrow placement="top" title={`Owned by ${prompt.owner?.displayName}`}>
          <AlineAvatar avatarInfo={prompt.owner} />
        </AlineTooltip>
      );
  }
}

interface PromptScopeIconProps {
  scope: Apollo.SavedPromptScope;
  style?: React.CSSProperties;
}

export function PromptScopeIcon({ scope, style }: PromptScopeIconProps): JSX.Element {
  switch (scope) {
    case Apollo.SavedPromptScope.Personal:
      return <UserSmallIcon style={style} />;
    case Apollo.SavedPromptScope.Aline:
      return <AlineSmallIcon style={style} />;
    case Apollo.SavedPromptScope.Org:
      return <PeopleSmallIcon style={style} />;
  }
}

interface PromptsListContentProps {
  activeTab: PromptTab;
  classes: ReturnType<typeof useStyles>['classes'];
  currentUserId: string | null;
  deleteLoading: boolean;
  editingPromptId: string | null;
  filteredPrompts: SavedPrompt[];
  loading: boolean;
  onDeletePrompt: (promptId: string, e: React.MouseEvent) => void;
  onPromptClick: (promptText: string, promptId: string, title: string, isEditable: boolean) => void;
  onShareClick: (
    promptId: string,
    options: {
      e: React.MouseEvent;
      scope: Apollo.SavedPromptScope;
    }
  ) => Promise<void>;
  onUpdateTitle: (promptId: string, newTitle: string) => Promise<void>;
  searchQuery: string;
}

function PromptsListContent({
  activeTab,
  classes,
  currentUserId,
  deleteLoading,
  editingPromptId,
  filteredPrompts,
  loading,
  onDeletePrompt,
  onPromptClick,
  onShareClick,
  onUpdateTitle,
  searchQuery,
}: PromptsListContentProps): JSX.Element {
  if (loading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          padding: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!filteredPrompts.length) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          padding: 4,
        }}
      >
        <Typography variant="subtitle2">
          {searchQuery ? 'No prompts match your search' : 'No saved prompts'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {filteredPrompts.map(prompt => (
        <PromptItem
          key={prompt.id}
          activeTab={activeTab}
          classes={classes}
          currentUserId={currentUserId}
          deleteLoading={deleteLoading}
          editingPromptId={editingPromptId}
          prompt={prompt}
          onDeletePrompt={onDeletePrompt}
          onPromptClick={onPromptClick}
          onShareClick={onShareClick}
          onUpdateTitle={onUpdateTitle}
        />
      ))}
    </>
  );
}

void gql`
  mutation UpdateSavedPromptScope($input: UpdateSavedPromptInput!) {
    updateSavedPrompt(input: $input) {
      savedPrompt {
        __typename
        id
        scope
      }
    }
  }
`;

void gql`
  mutation UpdateSavedPromptTitle($input: UpdateSavedPromptInput!) {
    updateSavedPrompt(input: $input) {
      savedPrompt {
        __typename
        id
        title
      }
    }
  }
`;

export interface ChatEditorSavePromptProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelectPrompt: (text: string, promptId: string, title: string, isEditable: boolean) => void;
  open: boolean;
  initialTab?: PromptTab;
}

export function ChatEditorSavePrompt({
  anchorEl,
  initialTab = 'my-saved',
  onClose,
  onSelectPrompt,
  open,
}: ChatEditorSavePromptProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const marginThresholdPx = 8;
  const currentUserId = useCurrentUserId();
  const chatState = useSnapshot($chatThreadState);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<PromptTab>(initialTab);
  const { maxHeight, openAbove, width } = usePopoverDimensions(anchorEl, {
    marginThresholdPx,
  });

  // Reset to initial tab when popover opens or when initialTab changes
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  function getScopeFromTab(tab: PromptTab): Apollo.SavedPromptScope | undefined {
    switch (tab) {
      case 'my-saved':
        return Apollo.SavedPromptScope.Personal;
      case 'aline':
        return Apollo.SavedPromptScope.Aline;
      case 'org':
        return Apollo.SavedPromptScope.Org;
    }
  }

  const [updateSavedPromptTitle] = Apollo.useUpdateSavedPromptTitleMutation();
  const [updateSavedPromptScope] = Apollo.useUpdateSavedPromptScopeMutation();
  const { data, loading } = Apollo.useGetSavedPromptsQuery({
    // Ensure that prompts showing in tabs are up-to-date w/ the server when tab is changed.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !open,
    variables: { scope: getScopeFromTab(activeTab) },
  });
  const [deleteSavedPrompt, { loading: deleteLoading }] = Apollo.useDeleteSavedPromptMutation();

  const filteredPrompts = useMemo(() => {
    const prompts = data?.savedPrompts ?? [];
    if (!searchQuery.trim()) return [...prompts].sort((a, b) => a.title.localeCompare(b.title));

    return matchSorter(prompts, searchQuery, {
      keys: ['title'] satisfies Array<keyof SavedPrompt>,
    });
  }, [data?.savedPrompts, searchQuery]);

  const handleDeletePrompt = async (promptId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    // If deleting the currently editing prompt, exit edit mode
    if (promptId === $chatThreadState.editingPromptId) {
      $chatThreadState.editingPromptId = null;
      $chatThreadState.editingPromptIsEditable = true;
      $chatThreadState.editingPromptOriginalText = null;
      $chatThreadState.editingPromptTitle = null;
    }

    try {
      await deleteSavedPrompt({
        optimisticResponse: {
          __typename: 'Mutation',
          deleteSavedPrompt: {
            __typename: 'DeleteSavedPromptResponse',
            savedPromptId: promptId,
          },
        },
        update: cache => {
          // Evict the deleted prompt from the cache
          cache.evict({ id: cache.identify({ __typename: 'SavedPrompt', id: promptId }) });
          cache.gc();
        },
        variables: { id: promptId },
      });
    } catch (err) {
      console.error('Failed to delete prompt:', err);
    }
  };

  const handleShareClick = async (
    promptId: string,
    options: {
      e: React.MouseEvent;
      scope: Apollo.SavedPromptScope;
    }
  ) => {
    const { e, scope } = options;

    e.stopPropagation();
    await updateSavedPromptScope({
      optimisticResponse: {
        __typename: 'Mutation',
        updateSavedPrompt: {
          __typename: 'SavedPromptResponse',
          savedPrompt: {
            __typename: 'SavedPrompt',
            id: promptId,
            scope,
          },
        },
      },
      variables: {
        input: {
          id: promptId,
          props: {
            scope,
          },
        },
      },
    });
  };

  const handleUpdateTitle = async (promptId: string, newTitle: string) => {
    // Update toolbar title if editing the same prompt
    if (promptId === $chatThreadState.editingPromptId) {
      $chatThreadState.editingPromptTitle = newTitle;
    }

    await updateSavedPromptTitle({
      optimisticResponse: {
        __typename: 'Mutation',
        updateSavedPrompt: {
          __typename: 'SavedPromptResponse',
          savedPrompt: {
            __typename: 'SavedPrompt',
            id: promptId,
            title: newTitle,
          },
        },
      },
      variables: {
        input: {
          id: promptId,
          props: {
            title: newTitle,
          },
        },
      },
    });
  };

  return (
    <Popover
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'top' : 'bottom',
      }}
      classes={{ paper: classes.popoverPaper }}
      marginThreshold={marginThresholdPx}
      open={open}
      transformOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'bottom' : 'top',
      }}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.5),
          maxHeight,
          padding: theme.spacing(1, 2),
          width,
        }}
      >
        {/* Header with title */}
        <div className={classes.header}>
          <Typography
            className={classes.headerTitle}
            sx={{
              fontWeight: 'bold',
            }}
            variant="body1"
          >
            Saved Prompts
          </Typography>
        </div>

        {/* Search input */}
        <SearchInput
          autoFocus
          inputClassName={classes.searchInput}
          minWidth={0}
          placeholder="Search prompts..."
          search={searchQuery}
          setSearch={setSearchQuery}
        />

        {/* Tabs */}
        <div className={classes.tabsContainer}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <Box
                key={tab.id}
                className={clsx(classes.tab, {
                  [classes.tabActive]: isActive,
                  [classes.tabInactive]: !isActive,
                })}
                onClick={() => setActiveTab(tab.id)}
              >
                <Box sx={{ display: 'flex' }}>
                  <Icon />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  variant="body2"
                >
                  {tab.label}
                </Typography>
              </Box>
            );
          })}
        </div>

        {/* Prompts list */}
        <div className={classes.promptsContainer}>
          <PromptsListContent
            activeTab={activeTab}
            classes={classes}
            currentUserId={currentUserId}
            deleteLoading={deleteLoading}
            editingPromptId={chatState.editingPromptId}
            filteredPrompts={filteredPrompts}
            loading={loading}
            searchQuery={searchQuery}
            onDeletePrompt={handleDeletePrompt}
            onPromptClick={onSelectPrompt}
            onShareClick={handleShareClick}
            onUpdateTitle={handleUpdateTitle}
          />
        </div>
      </div>
    </Popover>
  );
}
